"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { Check, X, Zap } from "lucide-react";

import { cn } from "@/lib/utils";
import { useLocale } from "@/lib/useLocale";
import { useDictionary } from "@/lib/useDictionary";

// 月付定期定額：相對年付單月價的加成倍率
const RECURRING_PRICE_MULTIPLIER = 1.1;

type BillingCycle = "monthly" | "yearly";

export default function PricingTable() {
  const locale = useLocale();
  const dict = useDictionary();

  const [billing, setBilling] = useState<BillingCycle>("yearly");
  const isYearly = billing === "yearly";

  const formatPrice = (value: number) =>
    value.toLocaleString(locale === "en" ? "en-US" : "zh-TW");

  const getDisplayPrice = (priceYearly: number) =>
    isYearly ? priceYearly : Math.round(priceYearly * RECURRING_PRICE_MULTIPLIER);

  const billingOptions: { key: BillingCycle; label: string; badge?: string }[] = [
    { key: "monthly", label: dict.pricing.billingMonthly },
    { key: "yearly", label: dict.pricing.billingYearly, badge: dict.pricing.yearlyDiscountBadge },
  ];

  return (
    <section className="py-20 bg-[var(--darker-blue)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-2 text-[var(--blue-900)]">
            <span className="text-3xl font-bold sm:text-4xl lg:text-5xl">{dict.pricing.freeTrial}</span>
            <motion.span className="hero-ink text-6xl font-black sm:text-7xl lg:text-7xl">
              14
            </motion.span>
            <span className="text-3xl font-bold sm:text-4xl lg:text-5xl">{dict.pricing.days}</span>
          </h2>

          <div className="mt-10 flex justify-center">
            <div className="relative inline-flex items-center gap-1 rounded-full border border-white/60 bg-white/45 p-2 backdrop-blur-2xl">
              {billingOptions.map((option) => {
                const active = billing === option.key;
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setBilling(option.key)}
                    className={cn(
                      "relative z-10 flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-300 sm:text-base",
                      active ? "text-white" : "text-[var(--blue-900)] hover:text-[var(--blue-700)]",
                    )}
                  >
                    {active && (
                      <motion.span
                        layoutId="billingPill"
                        transition={{ type: "spring", stiffness: 320, damping: 30 }}
                        className="absolute inset-0 -z-10 rounded-full bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-700"
                      />
                    )}
                    <span>{option.label}</span>
                    {option.badge && (
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[12px] font-bold leading-none transition-colors duration-300",
                          active
                            ? "bg-white/25 text-white"
                            : "bg-[var(--blue-700)]/10 text-[var(--blue-700)]",
                        )}
                      >
                        {option.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {dict.pricingPlans.map((plan, index) => (
            <motion.div
              key={`${plan.name}-${index}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className={cn(
                "relative bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300",
                plan.isRecommended ? "shadow-xl scale-105 z-10" : "hover:shadow-xl z-10",
              )}
            >
              {plan.isRecommended && (
                <div className="absolute top-0 left-0 right-0 gradient-accent text-gray-800 text-center py-2 text-sm font-semibold">
                  <div className="flex items-center justify-center gap-1">
                    <Zap className="w-4 h-4" />
                    {dict.pricing.mostPopular}
                  </div>
                </div>
              )}

              <div className={cn("p-8", plan.isRecommended && "pt-14")}>
                <div className="mb-6">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <h3 className="text-xl font-bold text-[var(--blue-900)]">{plan.name}</h3>
                    {plan.showAiMcpBadge ? (
                      <span className="ai-mcp-badge px-3 py-2">
                        {dict.pricing.aiMcpBadgeLabel}
                        <span className="ai-mcp-badge__shine" />
                      </span>
                    ) : null}
                  </div>
                  <p className="text-gray-500 text-sm">{plan.description}</p>
                </div>

                <div className="mb-8">
                  <div className="flex items-baseline gap-1">
                    <span className="text-lg text-gray-500">{plan.currency}</span>
                    <motion.span
                      key={billing}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25 }}
                      className="text-5xl font-bold text-[var(--blue-900)]"
                    >
                      {formatPrice(getDisplayPrice(plan.priceYearly))}
                    </motion.span>
                    <span className="text-gray-500">
                      {isYearly ? dict.pricing.perYear : dict.pricing.perMonth}
                    </span>
                  </div>
                </div>

                <Link
                  href="https://admin.quickdash.com.tw/"
                  className={cn(
                    "block w-full py-3 px-6 text-center font-semibold rounded-full transition-all duration-300",
                    plan.isRecommended
                      ? "gradient-accent text-gray-800 shadow-sm hover:shadow-xl hover:scale-105"
                      : "bg-[var(--darker-blue)] text-[var(--blue-700)] hover:bg-[var(--blue-700)] hover:text-white",
                  )}
                >
                  {plan.ctaText}
                </Link>

                <div className="mt-8 pt-8 border-t border-gray-100">
                  <ul className="space-y-3">
                    {plan.features.map((item) => (
                      <li
                        key={item.feature}
                        className={cn(
                          "flex items-center gap-3 text-sm",
                          item.included ? "text-gray-700" : "text-gray-400",
                        )}
                      >
                        {item.included ? (
                          <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-green-600" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <X className="w-3 h-3 text-gray-400" />
                          </div>
                        )}
                        {item.feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

<motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-32 text-center"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[var(--blue-900)] mb-8">
            {dict.pricing.needHelp}
          </h2>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12">
            <Link
              href="https://line.me/R/ti/p/@070xojro?ts=12171155&oat_content=url"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex hover:scale-105 hover:text-[var(--blue-500)] items-center gap-2 text-[var(--blue-700)] font-semibold transition-colors"
            >
              <Image
                src="/icon/line.png"
                alt="LINE"
                width={100}
                height={100}
                className="w-8 h-8 object-contain"
              />
              {dict.pricing.contactLine}
              <span className="text-xl">→</span>
            </Link>
            <Link
              href="https://m.me/305575275979962" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex hover:scale-105 hover:text-[var(--blue-500)] items-center gap-2 text-[var(--blue-700)] font-semibold transition-colors"
            >
              <Image
                src="/icon/msg.png" 
                alt="Facebook Messenger"
                width={100}
                height={100}
                className="w-7 h-7 object-contain"
              />
              {dict.pricing.contactMessenger} 
              <span className="text-xl">→</span>
            </Link>

          </div>
        </motion.div>
      </div>
    </section>
  );
}
